import { Component, HostListener, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { OpfoelgningService, ServiceAftaleService, ServiceordreService } from 'src/app/api/services';
import { LoaderService } from 'src/app/services/loader.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { catchError, finalize, flatMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { HttpErrorResponse } from '@angular/common/http';
import { defer, of, Subject } from 'rxjs';
import { ServiceordreAfregningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-afregning-dto';
import { UpdateServiceordreRequestDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/update-serviceordre-request-dto';
import { ServiceordreWithAfregningResponseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-with-afregning-response-dto';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import {
  CancelServiceordreDialogData,
  CancelServiceordreDialogResponse,
  ChangeOpgaveTypeDialogData,
  ConfirmDialogData,
  OpfoelgningAction,
  OpfoelgningDialogResponse,
  RejectionData,
} from '../../aftale/models';
import { MatSelectionListChange } from '@angular/material/list';
import { OrdreService } from 'src/app/services/ordre.service';
import { RejectPosteringerDialogComponent } from '../reject-posteringer-dialog/reject-posteringer-dialog.component';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { OpfoelgningChanged, SignalrService } from 'src/app/services/signalr.service';
import { ChangeOrderTypeDialogComponent } from './change-order-type-dialog/change-order-type-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import { AuditHistoryEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/audit-history-enum';
import { AuditHistoryType } from '../../aftale/details/models/selectfiltermodels';
import { FilterOption } from '../../shared/history/select-filter/select-filter.component';
import { PartnerDialogComponent } from '../../shared/partner-dialog/partner-dialog.component';
import { inOutAnimation } from 'src/app/common/animations';
import { CancelOrdreDialogComponent } from '../cancel-ordre-dialog/cancel-ordre-dialog.component';
import { ServiceordreStatusEnum } from '../../../api/models/OK/Egis/Service/Models/Entities/Enums/serviceordre-status-enum';
import { ShowPartnerPayloadComponent } from '../../ordre/edit-ordre/partner-payload/partner-payload.component';

@Component({
  selector: 'app-edit-ordre',
  templateUrl: './edit-ordre.component.html',
  styleUrls: ['./edit-ordre.component.scss'],
  animations: [inOutAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditOrdreComponent implements OnInit {
  serviceordreStatusEnum = ServiceordreStatusEnum;

  serviceordreNo: number;
  serviceordre: ServiceordreDto;
  serviceordrer: ServiceordreDto[] = [];
  serviceOrdreAfregning: ServiceordreAfregningDto;
  serviceordreWithAfregning: ServiceordreWithAfregningResponseDto;
  serviceAftale: ServiceaftaleDto;
  page: string;
  isAdmin: boolean = false;
  isSuperUser: boolean = false;
  canWrite: boolean = false;
  hasRights: boolean = false;
  canCancelPosteringer: boolean = false;
  canGodkendePosteringer: boolean = false;
  quickViewOpen: boolean = false;

  readonly entityType: AuditHistoryEnum = AuditHistoryEnum.Serviceordre;

  unsubscribe$ = new Subject<void>();

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.quickViewOpen) {
        this.closeQuickView();
      }
    }
  }

  constructor(
    private serviceordreService: ServiceordreService,
    private serviceAftaleService: ServiceAftaleService,
    private route: ActivatedRoute,
    private notifications: NotificationService,
    private loader: LoaderService,
    public dialog: MatDialog,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    private ordreService: OrdreService,
    private notification: NotificationService,
    private opfoelgningService: OpfoelgningService,
    private signalR: SignalrService,
    private auth: AuthService,
  ) {
    this.page = this.route.snapshot.fragment ? this.route.snapshot.fragment : 'Detaljer';
    this.isAdmin = this.auth.isAdministrator;
    this.isSuperUser = this.auth.isSuperuser;
    this.hasRights = this.auth.isAdministrator || this.auth.isSuperuser || this.auth.isKundeservice;
    this.canWrite = this.auth.canWrite;
    this.canCancelPosteringer = this.auth.isAdministrator || this.auth.isSuperuser;
    this.canGodkendePosteringer = this.auth.isAdministrator || this.auth.isSuperuser;
  }

  selectionChanged(change: MatSelectionListChange) {
    this.router.navigate(['ordre', this.serviceordre.serviceordreNo, 'edit'], { fragment: change.options[0].value });
    this.page = change.options[0].value;
  }

  ngOnInit(): void {
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.page = this.route.snapshot.fragment ? this.route.snapshot.fragment : 'Detaljer';
    });

    this.route.params
      .pipe(
        switchMap((params: Params) => {
          const serviceordreNo = params.serviceordreNo;
          if (serviceordreNo) {
            this.serviceordreNo = Number(serviceordreNo);
          }
          return this.getData$;
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.signalR.opfoelgningChanged$.pipe(takeUntil(this.unsubscribe$)).subscribe((event: OpfoelgningChanged) => {
      if (this.serviceordre.serviceordreNo == event.entityNo) {
        this.serviceordre.opfoelgning = event.opfoelgning;
      }
    });

    this.signalR.serviceordreChanged$
      .pipe(
        switchMap((serviceordre: ServiceordreDto) => {
          if (this.serviceordre.serviceordreNo !== serviceordre.serviceordreNo) {
            return of(null);
          }
          this.serviceordre = serviceordre;
          return this.getData$;
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  getData$ = defer(() => {
    this.loader.show();
    return this.serviceordreService
      .getServiceordreWithAfregningsInfo({
        caller: EGIS_SERVICE_CALLER,
        serviceordreNo: this.serviceordreNo,
      })
      .pipe(
        flatMap((serviceordreAndAfregning) => {
          this.serviceordreWithAfregning = serviceordreAndAfregning;
          this.serviceordre = serviceordreAndAfregning.serviceOrdre;
          this.serviceOrdreAfregning = serviceordreAndAfregning.serviceOrdreAfregning;
          return this.serviceAftaleService
            .getServiceaftaleByNo({
              caller: EGIS_SERVICE_CALLER,
              serviceaftaleNo: serviceordreAndAfregning.serviceOrdre.serviceaftaleNo,
            })
            .pipe(
              flatMap((serviceaftale) => {
                this.serviceAftale = serviceaftale;
                return this.serviceordreService
                  .getServiceordrer({
                    caller: EGIS_SERVICE_CALLER,
                    body: {
                      serviceaftaleNoList: [this.serviceAftale.serviceAftaleNo],
                      serviceordreStateDtoIds: [1, 2, 3, 9, 10, 11, 12, 13],
                    },
                  })
                  .pipe(
                    tap((result) => {
                      this.serviceordrer = result.serviceordrer;
                    }),
                  );
              }),
            );
        }),
        finalize(() => {
          this.loader.hide();
        }),
      );
  });

  auditHistoryTypesKeys = Object.keys(AuditHistoryType)
    .filter((p) => p != AuditHistoryType.Bemærkninger.toString())
    .filter(Number)
    .map(([id]) => new FilterOption(parseInt(id), AuditHistoryType[id], parseInt(id) === AuditHistoryType.Alt));

  selectedAuditHistoryType: number = Number(AuditHistoryType.Alt);

  ApproveOrdre(serviceordre: ServiceordreDto) {
    if (serviceordre.serviceordreAfregning.serviceordreAfregningPostList.some((x) => x.betaler.id > 1)) {
      let dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Kunde bliver ikke opkrævet på alle posteringer.',
          body: 'Er du sikker på du vil godkende?',
        },
      });

      dialogRef.afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.ApproveOrdreAfter(serviceordre);
        }
      });
    } else {
      this.ApproveOrdreAfter(serviceordre);
    }
  }

  ApproveOrdreAfter(serviceordre: ServiceordreDto) {
    this.loader.show();
    this.serviceordreService
      .approveForAccountingServiceordre({
        caller: EGIS_SERVICE_CALLER,
        serviceordreNo: serviceordre.serviceordreNo,
      })
      .pipe(
        tap(() => {}),
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {
          this.notifications.success('Ordren blev godkendt');
          window.history.back();
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  RetractApproval(serviceordre: ServiceordreDto) {
    this.loader.show();
    this.serviceordreService
      .retractApproval({
        caller: EGIS_SERVICE_CALLER,
        serviceordreNo: serviceordre.serviceordreNo,
      })
      .pipe(
        tap(() => {}),
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (result: ServiceordreDto) => {
          this.serviceordre = result;
          this.notifications.success('Godkendelse af ordren er nu fortrudt');
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  RequeueOrdreForAccounting(serviceordre: ServiceordreDto) {
    this.loader.show();
    this.serviceordreService
      .requeueForAccounting({
        caller: EGIS_SERVICE_CALLER,
        serviceordreNo: serviceordre.serviceordreNo,
      })
      .pipe(
        tap(() => {}),
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {
          this.notifications.success('Ordren blev sat til afregning igen');
          window.history.back();
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  resendOrdre() {
    this.ordreService.create(this.serviceordre.serviceordreType, this.serviceAftale, this.serviceordre).subscribe(
      () => {
        this.router.navigate(['/aftale', this.serviceAftale.okKundeAftaleNr], { fragment: 'Ordrer' });
      },
      (error) => {
        this.errorHandler.HandleError(error);
      },
    );
  }

  GensendOrdre(serviceordre: ServiceordreDto) {
    if (
      serviceordre.serviceordreStateEnum != ServiceordreStatusEnum.Broadcasted &&
      serviceordre.serviceordreStateEnum != ServiceordreStatusEnum.ReadyForBroadcast &&
      serviceordre.serviceordreStateEnum != ServiceordreStatusEnum.BroadcastFailed
    ) {
      this.notifications.warning('Ordren kan ikke gensendes');
      return;
    }

    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Vil du fortsætte?',
        body: `Dette vil sende en kopi til s-partner, som umiddelbart kun er relevant, hvis s-partner har mistet/slettet serviceordre?`,
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.loader.show();
        this.serviceordreService
          .resendServiceordreToPartner({
            caller: EGIS_SERVICE_CALLER,
            serviceordreEntityId: serviceordre.id,
          })
          .pipe(
            tap(() => {}),
            finalize(() => {
              this.loader.hide();
            }),
            take(1),
          )
          .subscribe(
            () => {
              this.notifications.success('Ordren blev gensendt til s-partner');
            },
            catchError((err) => {
              this.errorHandler.HandleError(err);
              return of(null);
            }),
          );
      }
    });
  }

  ChangeOrderType(serviceordre: ServiceordreDto) {
    var dialogData: ChangeOpgaveTypeDialogData = {
      opgaveType: serviceordre.serviceordreType,
      serviceOrdreNo: serviceordre.serviceordreNo,
    };
    let dialogRef = this.dialog.open(ChangeOrderTypeDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((order: ServiceordreDto) => {
      if (order) {
        this.serviceordre = order;
        this.notifications.success('Ordren er skiftet til ' + order.opgaveDisplayText);
      }
    });
  }

  get CanCancel(): boolean {
    return (
      this.serviceordre.serviceordreStateEnum == ServiceordreStatusEnum.Broadcasted ||
      this.serviceordre.serviceordreStateEnum == ServiceordreStatusEnum.ReadyForBroadcast ||
      this.serviceordre.serviceordreStateEnum == ServiceordreStatusEnum.BroadcastFailed ||
      this.serviceordre.serviceordreStateEnum == ServiceordreStatusEnum.CompletedWithRejection
    );
  }

  CancelOrdre(serviceordre: ServiceordreDto) {
    let data: CancelServiceordreDialogData = {
      produkt: this.serviceAftale.serviceAbonnement.product.id,
      ordreType: serviceordre.serviceordreType,
    };
    let dialogRef = this.dialog.open(CancelOrdreDialogComponent, { data: data });

    dialogRef.afterClosed().subscribe((confirm: CancelServiceordreDialogResponse) => {
      if (confirm) {
        this.loader.show();
        this.serviceordreService
          .cancelServiceordre({
            caller: EGIS_SERVICE_CALLER,
            serviceordreNo: serviceordre.serviceordreNo,
            note: confirm.note,
            resendToNewPatner: confirm.resendToNewPartner,
            newPartnerNo: confirm.newPartnerNo,
          })
          .pipe(
            finalize(() => {
              this.loader.hide();
            }),
            takeUntil(this.unsubscribe$),
          )
          .subscribe(
            () => {
              window.history.back();
            },
            (error: HttpErrorResponse) => {
              this.errorHandler.HandleError(error);
            },
          );
      }
    });
  }

  rejectPosteringer() {
    const dialogRef = this.dialog.open(RejectPosteringerDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
    });
    dialogRef.afterClosed().subscribe((rejection: RejectionData) => {
      if (rejection.confirm) {
        this.loader.show();
        this.serviceordreService
          .afvisPosteringerForServiceordre({
            caller: EGIS_SERVICE_CALLER,
            body: { remark: rejection.rejectionRemark, serviceordreNo: this.serviceordre.serviceordreNo },
          })
          .pipe(
            finalize(() => {
              this.loader.hide();
            }),
            takeUntil(this.unsubscribe$),
            flatMap(() => {
              return this.getData$;
            }),
          )
          .subscribe(
            () => {},
            (error) => {
              this.errorHandler.HandleError(error);
            },
          );
      }
    });
  }

  getProductIcon(serviceordre: ServiceordreDto) {
    return serviceordre.produkt.toLowerCase();
  }

  UpdateOrdre(update: UpdateServiceordreRequestDto) {
    this.loader.show();
    this.serviceordreService
      .updateServiceordre({ body: update, caller: EGIS_SERVICE_CALLER })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
        flatMap((response: ServiceordreWithAfregningResponseDto) => {
          return this.getData$;
        }),
      )
      .subscribe(
        () => {
          this.notifications.success('Ordren er opdateret');
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  onCloseClick() {
    window.history.back();
  }

  visAftale() {
    this.quickViewOpen = true;
  }

  closeQuickView() {
    this.quickViewOpen = false;
  }

  onAftaleOpfoelgningChanged(response: OpfoelgningDialogResponse) {
    if (response.action == OpfoelgningAction.Upsert) {
      this.loader.show();

      this.opfoelgningService
        .upsertServiceaftaleOpfoelgning({
          caller: EGIS_SERVICE_CALLER,
          body: {
            id: response.opfoelgning.id,
            note: response.opfoelgning.note,
            deadline: response.opfoelgning.deadline ? new Date(response.opfoelgning.deadline).toOkDateString() : null,
            prioritet: response.opfoelgning.priority,
            entityNo: response.opfoelgning.entityNo,
          },
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er gemt.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    } else if (response.action == OpfoelgningAction.Delete) {
      this.opfoelgningService
        .removeServiceaftaleOpfoelgning({ caller: EGIS_SERVICE_CALLER, id: response.opfoelgning.id })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er fjernet.');
            const sa = this.serviceAftale;
            if (sa) {
              let index = sa.opfoelgningList.findIndex((p) => p.id == response.opfoelgning.id);
              if (index >= 0) {
                sa.opfoelgningList.splice(index, 1);
              }
            }
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    }
  }

  onOpfoelgningChanged(response: OpfoelgningDialogResponse) {
    if (response.action == OpfoelgningAction.Upsert) {
      this.loader.show();

      this.opfoelgningService
        .upsertServiceordreOpfoelgning({
          caller: EGIS_SERVICE_CALLER,
          body: {
            id: response.opfoelgning.id,
            note: response.opfoelgning.note,
            deadline: response.opfoelgning.deadline ? new Date(response.opfoelgning.deadline).toOkDateString() : null,
            prioritet: response.opfoelgning.priority,
            entityNo: response.opfoelgning.entityNo,
            parker: response.park,
          },
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er gemt.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    } else if (response.action == OpfoelgningAction.Delete) {
      this.opfoelgningService
        .removeServiceordreOpfoelgning({ caller: EGIS_SERVICE_CALLER, id: response.opfoelgning.id })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er fjernet.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    }
  }

  openPartnerInfo() {
    this.loader.show();
    this.serviceordreService.getPartnerInfoForServiceordre({ serviceordreNo: this.serviceordreNo, caller: EGIS_SERVICE_CALLER }).subscribe(
      (partnerInfo) => {
        this.loader.hide();
        this.dialog.open(PartnerDialogComponent, {
          data: partnerInfo,
        });
      },
      (error: HttpErrorResponse) => {
        this.loader.hide();
        this.errorHandler.HandleError(error);
      },
    );
  }

  showPartnerPayload(partnerPayload: string, receivedDate: string) {
    this.dialog.open(ShowPartnerPayloadComponent, {
      width: '1400px',
      maxWidth: '95vw',
      data: { payload: partnerPayload, receivedDate: receivedDate },
    });
  }
}

function CancelServiceordreDialogComponent(CancelServiceordreDialogComponent: any, arg1: { data: ConfirmDialogData }) {
  throw new Error('Function not implemented.');
}
